if (!customElements.get('size-dropdown')) {
  customElements.define(
    'size-dropdown',
    /**
     * Select-only combobox (WAI-ARIA APG pattern) in front of the native size <select>. The list always opens below
     * the field and the options can wrap. The select stays in the DOM and keeps doing the work: picking an option sets
     * its value and dispatches the change event that variant-selects.js, product-info.js and Kbite (via ?variant=)
     * already rely on. Without JavaScript the native select is used.
     */
    class SizeDropdown extends HTMLElement {
      // product-info.js replaces the whole picker after a change; the new dropdown takes focus back if its select has
      // this id and the change happened less than 5 seconds ago.
      static refocus = { id: null, at: 0 };

      activeIndex = -1;
      typeaheadBuffer = '';

      connectedCallback() {
        this.select = this.querySelector(':scope > select');
        if (!this.select || this.combobox) return;

        this.render();
        this.classList.add('size-dropdown--enhanced');

        const { id, at } = SizeDropdown.refocus;
        if (id === this.select.id && Date.now() - at < 5000) {
          SizeDropdown.refocus = { id: null, at: 0 };
          this.combobox.focus();
        }
      }

      disconnectedCallback() {
        document.removeEventListener('pointerdown', this.onOutsidePointerDown);
        clearTimeout(this.typeaheadTimer);
      }

      get isOpen() {
        return !this.listbox.hidden;
      }

      render() {
        const baseId = this.select.id;
        const labelId = this.dataset.labelId;

        this.combobox = document.createElement('div');
        this.combobox.id = `${baseId}-combobox`;
        this.combobox.className = 'size-dropdown__combobox form-control form-control--select';
        this.combobox.tabIndex = 0;
        this.combobox.setAttribute('role', 'combobox');
        this.combobox.setAttribute('aria-haspopup', 'listbox');
        this.combobox.setAttribute('aria-expanded', 'false');
        this.combobox.setAttribute('aria-controls', `${baseId}-listbox`);
        if (labelId) this.combobox.setAttribute('aria-labelledby', labelId);

        this.listbox = document.createElement('ul');
        this.listbox.id = `${baseId}-listbox`;
        this.listbox.className = 'size-dropdown__listbox';
        this.listbox.tabIndex = -1;
        this.listbox.hidden = true;
        this.listbox.setAttribute('role', 'listbox');
        if (labelId) this.listbox.setAttribute('aria-labelledby', labelId);

        this.items = Array.from(this.select.options, (option, index) => {
          const item = document.createElement('li');
          item.id = `${baseId}-option-${index}`;
          item.className = 'size-dropdown__option';
          item.classList.toggle('is-sold-out', option.classList.contains('is-sold-out'));
          item.setAttribute('role', 'option');
          item.setAttribute('aria-selected', String(option.selected));
          item.textContent = option.textContent.trim();
          return item;
        });
        this.listbox.append(...this.items);
        this.combobox.textContent = this.items[this.select.selectedIndex]?.textContent || '';
        this.select.tabIndex = -1;
        this.select.after(this.combobox, this.listbox);

        this.combobox.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
        this.combobox.addEventListener('keydown', this.onKeydown.bind(this));
        this.combobox.addEventListener('focusout', (event) => {
          if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close(false);
        });
        // Keep focus on the combobox while an option is clicked.
        this.listbox.addEventListener('mousedown', (event) => event.preventDefault());
        this.listbox.addEventListener('click', (event) => {
          const item = event.target.closest('[role="option"]');
          if (item) this.choose(this.items.indexOf(item));
        });
        this.listbox.addEventListener('pointermove', (event) => {
          const item = event.target.closest('[role="option"]');
          if (item && event.pointerType === 'mouse') this.setActive(this.items.indexOf(item), false);
        });
      }

      onOutsidePointerDown = (event) => {
        if (!this.contains(event.target)) this.close(false);
      };

      open(index = this.select.selectedIndex) {
        if (!this.isOpen) {
          this.listbox.hidden = false;
          this.combobox.setAttribute('aria-expanded', 'true');
          this.setAttribute('open', '');
          document.addEventListener('pointerdown', this.onOutsidePointerDown);
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.listbox.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
        }
        this.setActive(index < 0 ? 0 : index);
      }

      close(refocus = true) {
        if (!this.isOpen) return;
        this.listbox.hidden = true;
        this.combobox.setAttribute('aria-expanded', 'false');
        this.combobox.removeAttribute('aria-activedescendant');
        this.removeAttribute('open');
        document.removeEventListener('pointerdown', this.onOutsidePointerDown);
        if (refocus) this.combobox.focus();
      }

      setActive(index, scroll = true) {
        const next = Math.max(0, Math.min(index, this.items.length - 1));
        this.items[this.activeIndex]?.classList.remove('is-active');
        this.activeIndex = next;

        const item = this.items[next];
        if (!item) return;
        item.classList.add('is-active');
        this.combobox.setAttribute('aria-activedescendant', item.id);

        if (!scroll) return;
        // Scroll the list only; item.scrollIntoView() would also scroll the page.
        const { offsetTop, offsetHeight } = item;
        if (offsetTop < this.listbox.scrollTop) {
          this.listbox.scrollTop = offsetTop;
        } else if (offsetTop + offsetHeight > this.listbox.scrollTop + this.listbox.clientHeight) {
          this.listbox.scrollTop = offsetTop + offsetHeight - this.listbox.clientHeight;
        }
      }

      choose(index, refocus = true) {
        this.close(refocus);
        const option = this.select.options[index];
        if (!option || option.selected) return;

        this.combobox.textContent = this.items[index].textContent;
        this.items.forEach((item, i) => item.setAttribute('aria-selected', String(i === index)));
        SizeDropdown.refocus = refocus ? { id: this.select.id, at: Date.now() } : { id: null, at: 0 };

        this.select.value = option.value;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onKeydown(event) {
        const { key, altKey, ctrlKey, metaKey } = event;
        const last = this.items.length - 1;

        if (!this.isOpen) {
          if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(key)) {
            event.preventDefault();
            this.open();
          } else if (key === 'Home' || key === 'End') {
            event.preventDefault();
            this.open(key === 'Home' ? 0 : last);
          } else if (key.length === 1 && !ctrlKey && !metaKey && !altKey) {
            this.open();
            this.typeahead(key);
          }
          return;
        }

        switch (key) {
          case 'ArrowDown':
            event.preventDefault();
            this.setActive(this.activeIndex + 1);
            break;
          case 'ArrowUp':
            event.preventDefault();
            if (altKey) this.choose(this.activeIndex);
            else this.setActive(this.activeIndex - 1);
            break;
          case 'Home':
          case 'PageUp':
            event.preventDefault();
            this.setActive(key === 'Home' ? 0 : this.activeIndex - 10);
            break;
          case 'End':
          case 'PageDown':
            event.preventDefault();
            this.setActive(key === 'End' ? last : this.activeIndex + 10);
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            this.choose(this.activeIndex);
            break;
          case 'Escape':
            event.preventDefault();
            this.close();
            break;
          case 'Tab':
            this.choose(this.activeIndex, false);
            break;
          default:
            if (key.length === 1 && !ctrlKey && !metaKey && !altKey) this.typeahead(key);
        }
      }

      // Typing jumps to the next option that starts with the typed text, e.g. "1" then "6" for 16⅞ oz.
      typeahead(char) {
        clearTimeout(this.typeaheadTimer);
        this.typeaheadTimer = setTimeout(() => (this.typeaheadBuffer = ''), 600);
        this.typeaheadBuffer += char.toLowerCase();

        const count = this.items.length;
        const start = this.typeaheadBuffer.length === 1 ? this.activeIndex + 1 : this.activeIndex;
        for (let step = 0; step < count; step++) {
          const index = (start + step) % count;
          if (this.items[index].textContent.toLowerCase().startsWith(this.typeaheadBuffer)) {
            this.setActive(index);
            return;
          }
        }
      }
    }
  );
}
